import ProductCard from "@/components/ProductCard";
import { Can } from "@/components/Can";
import BlankProductCard from "@/components/BlankProductCard";
import VendorScheduleWidget from "@/components/pos/VendorScheduleWidget";
import TodoWidget from "@/components/pos/TodoWidget";
import CashDropButton from "@/components/pos/CashDropButton";
import RemoteSupportButton from "@/components/pos/RemoteSupportButton";
import { Pause, Play, XCircle, RotateCcw, DollarSign, Printer, Loader2, Truck, Pencil, Search, Tag, UserPlus, UserSearch, ShieldCheck } from "lucide-react";

export default function ProductGrid({
  tabs,
  activeTab,
  setActiveTab,
  activeTabColor,
  loading,
  displaySettings,
  getButtonLayout,
  addToCart,
  handleBlankCardClick,
  handleLongPress,
  onSubButtonClick,
  // Bottom action bar
  heldTransactions,
  isRefundMode,
  isPrinting,
  isOpeningDrawer,
  handleHold,
  handleVoid,
  handleRefund,
  handleNoSale,
  onAddCustomer,
  onSearchCustomer,
  onAgeCheck,
  selectedCustomer,
  handlePrintLastReceipt,
  setShowVendorPayoutDialog,
  selectedCartItemId,
  openPriceChangeDialog,
  onLookup,
  onPriceCheck,
}) {
  return (
    <div className="flex flex-col gap-1 px-1 py-1 min-h-0 overflow-hidden" style={{ width: '62%', flexShrink: 0 }}>
      {/* Tabs */}
      <div
        className="flex-shrink-0"
        style={{
          backgroundColor: displaySettings.tabs_bg_color || "#FFFFFF",
          border: `2px solid ${displaySettings.tabs_border_color || "#E4E4E7"}`,
          borderRadius: "8px",
        }}
      >
        {tabs.length > 0 && (
          <div className="grid grid-cols-7 gap-2 px-4 py-2" data-testid="pos-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2 py-2 rounded-lg font-bold text-sm whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-lg"
                    : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                }`}
                style={activeTab === tab.id ? { backgroundColor: tab.color } : {}}
                data-testid={`tab-${tab.id}`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Products Grid */}
      <div
        className="flex-1 min-h-0 overflow-hidden"
        style={{
          backgroundColor: displaySettings.product_grid_bg_color || "#FFFFFF",
          padding: "4px",
          border: `2px solid ${displaySettings.product_grid_border_color || "#E4E4E7"}`,
          borderRadius: "8px",
        }}
      >
        {loading ? (
          <div className="text-center py-12">Loading products...</div>
        ) : (
          <div className="grid grid-cols-7 gap-1 h-full p-1.5" style={{ gridAutoRows: "1fr" }}>
            {getButtonLayout().map((item) => {
              if (item.isBlank) {
                return (
                  <BlankProductCard
                    key={`blank-${item.position}`}
                    onClick={() => handleBlankCardClick(item.position)}
                  />
                );
              } else {
                return (
                  <ProductCard
                    key={`${item.product.id}-${item.position}`}
                    product={item.product}
                    displayName={item.displayName}
                    onAdd={(product) => {
                      if (item.subButtons && item.subButtons.length > 0) {
                        onSubButtonClick({ parentProduct: item.product, subButtons: item.subButtons, position: item.position });
                      } else {
                        addToCart(product);
                      }
                    }}
                    onLongPress={(product) => handleLongPress(product, item.position)}
                    displaySettings={displaySettings}
                    tabColor={activeTabColor}
                    buttonStyleOverride={item.cardStyle}
                    buttonBorderOverride={item.borderColor}
                    hasSubButtons={item.subButtons && item.subButtons.length > 0}
                    buttonImage={item.buttonImage}
                    subButtonsImage={item.subButtonsImage}
                  />
                );
              }
            })}
            {/* Last row: Vendors (2 cols) | To-Do (2 cols) | reserved space (3 cols) */}
            <div className="col-span-2 min-h-0 overflow-hidden">
              <Can k="pos.widgets.vendors"><VendorScheduleWidget /></Can>
            </div>
            <div className="col-span-2 min-h-0 overflow-hidden">
              <Can k="pos.widgets.todo"><TodoWidget /></Can>
            </div>
            <div
              className="col-span-2 min-h-0 rounded-lg bg-zinc-200 border-2 border-black overflow-hidden relative"
              data-testid="pos-reserved-slot"
            >
              <div className="absolute bottom-1.5 right-1.5">
                <Can k="pos.side_actions.cash_drop"><CashDropButton /></Can>
              </div>
              <div className="absolute bottom-1.5 left-1.5">
                <Can k="pos.side_actions.remote_support"><RemoteSupportButton /></Can>
              </div>
            </div>
            <div className="col-span-1 min-h-0 flex flex-col gap-1" data-testid="customer-actions-card">
              <Can k="pos.side_actions.add_customer"><button onClick={onAddCustomer} className="flex-1 min-h-0 flex items-center justify-center gap-1 rounded-lg font-bold text-[12px] leading-tight px-1 bg-sky-600 text-white hover:bg-sky-700 transition-all hover:scale-[1.03] active:scale-95" data-testid="btn-add-customer" title="Add a new customer (scan ID)">
                <UserPlus className="w-4 h-4 shrink-0" /><span>+ Customer</span>
              </button></Can>
              <Can k="pos.side_actions.search_customer"><button onClick={onSearchCustomer} className={`flex-1 min-h-0 flex items-center justify-center gap-1 rounded-lg font-bold text-[12px] leading-tight px-1 transition-all hover:scale-[1.03] active:scale-95 ${selectedCustomer ? "bg-emerald-700 text-white ring-2 ring-emerald-300" : "bg-sky-700 text-white hover:bg-sky-800"}`} data-testid="btn-search-customer" title={selectedCustomer ? `Customer: ${selectedCustomer.full_name}` : "Find customer by phone"}>
                <UserSearch className="w-4 h-4 shrink-0" /><span className="text-center break-words">{selectedCustomer ? selectedCustomer.full_name.split(" ")[0] : "Search Customer"}</span>
              </button></Can>
              <Can k="pos.side_actions.age_check"><button onClick={onAgeCheck} className="flex-1 min-h-0 flex items-center justify-center gap-1 rounded-lg font-bold text-[12px] leading-tight px-1 bg-violet-600 text-white hover:bg-violet-700 transition-all hover:scale-[1.03] active:scale-95" data-testid="btn-age-check" title="Scan ID to verify age">
                <ShieldCheck className="w-4 h-4 shrink-0" /><span>Age Check</span>
              </button></Can>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div
        className="px-2 py-1 flex-shrink-0"
        style={{
          backgroundColor: displaySettings.bottom_buttons_bg_color || "#FFFFFF",
          border: `2px solid ${displaySettings.bottom_buttons_border_color || "#E4E4E7"}`,
          borderRadius: "8px",
        }}
      >
        <div className="grid grid-cols-9 gap-1.5">
          {/* Hold */}
          <Can k="pos.quick_actions.hold"><button
            onClick={handleHold}
            className={`flex flex-col items-center justify-center rounded-lg font-bold text-[14px] transition-all hover:scale-105 active:scale-95 ${
              heldTransactions.length > 0
                ? "bg-amber-500 text-white hover:bg-amber-600"
                : "bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            }`}
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-hold"
          >
            {heldTransactions.length > 0 ? <Play className="w-3.5 h-3.5 mb-0.5" /> : <Pause className="w-3.5 h-3.5 mb-0.5" />}
            <span>{heldTransactions.length > 0 ? `Retrieve (${heldTransactions.length})` : "Hold"}</span>
          </button></Can>

          {/* Void */}
          <Can k="pos.quick_actions.void"><button
            onClick={handleVoid}
            className="flex flex-col items-center justify-center rounded-lg font-bold text-[14px] bg-red-500 text-white hover:bg-red-600 transition-all hover:scale-105 active:scale-95"
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-void"
          >
            <XCircle className="w-3.5 h-3.5 mb-0.5" />
            <span>Void</span>
          </button></Can>

          {/* Refund */}
          <Can k="pos.quick_actions.refund"><button
            onClick={handleRefund}
            className={`flex flex-col items-center justify-center rounded-lg font-bold text-[14px] transition-all hover:scale-105 active:scale-95 ${
              isRefundMode
                ? "bg-red-600 text-white ring-2 ring-red-300"
                : "bg-orange-500 text-white hover:bg-orange-600"
            }`}
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-refund"
          >
            <RotateCcw className="w-3.5 h-3.5 mb-0.5" />
            <span>{isRefundMode ? "Exit Refund" : "Refund"}</span>
          </button></Can>

          {/* No Sale */}
          <Can k="pos.quick_actions.no_sale"><button
            onClick={handleNoSale}
            disabled={isOpeningDrawer}
            className={`flex flex-col items-center justify-center rounded-lg font-bold text-[14px] transition-all hover:scale-105 active:scale-95 ${
              isOpeningDrawer
                ? "bg-emerald-300 text-white cursor-wait"
                : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-no-sale"
          >
            {isOpeningDrawer ? <Loader2 className="w-3.5 h-3.5 mb-0.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5 mb-0.5" />}
            <span>{isOpeningDrawer ? "Opening..." : "No Sale"}</span>
          </button></Can>

          {/* Print Last */}
          <Can k="pos.quick_actions.print_last"><button
            onClick={handlePrintLastReceipt}
            disabled={isPrinting}
            className={`flex flex-col items-center justify-center rounded-lg font-bold text-[14px] transition-all hover:scale-105 active:scale-95 ${
              isPrinting
                ? "bg-blue-300 text-white cursor-wait"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-print-receipt"
          >
            {isPrinting ? <Loader2 className="w-3.5 h-3.5 mb-0.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mb-0.5" />}
            <span>{isPrinting ? "Printing..." : "Print Last"}</span>
          </button></Can>

          {/* Vendor Payout */}
          <Can k="pos.quick_actions.vendor_payout"><button
            onClick={() => setShowVendorPayoutDialog(true)}
            className="flex flex-col items-center justify-center rounded-lg font-bold text-[14px] bg-purple-500 text-white hover:bg-purple-600 transition-all hover:scale-105 active:scale-95"
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-vendor-payout"
          >
            <Truck className="w-3.5 h-3.5 mb-0.5" />
            <span>Vendor Payout</span>
          </button></Can>

          {/* Price Change */}
          <Can k="pos.quick_actions.price_change"><button
            onClick={openPriceChangeDialog}
            disabled={!selectedCartItemId}
            className={`flex flex-col items-center justify-center rounded-lg font-bold text-[14px] transition-all hover:scale-105 active:scale-95 ${
              selectedCartItemId
                ? "bg-zinc-900 text-white hover:bg-black"
                : "bg-zinc-200 border border-zinc-300 text-zinc-400 cursor-not-allowed"
            }`}
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-price-change"
          >
            <Pencil className="w-3.5 h-3.5 mb-0.5" />
            <span>Price Change</span>
          </button></Can>

          {/* Lookup */}
          <Can k="pos.quick_actions.lookup"><button
            onClick={onLookup}
            className="flex flex-col items-center justify-center rounded-lg font-bold text-[14px] bg-cyan-600 text-white hover:bg-cyan-700 transition-all hover:scale-105 active:scale-95"
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-lookup"
          >
            <Search className="w-3.5 h-3.5 mb-0.5" />
            <span>Lookup</span>
          </button></Can>

          {/* Price Check */}
          <Can k="pos.quick_actions.price_check"><button
            onClick={onPriceCheck}
            className="flex flex-col items-center justify-center rounded-lg font-bold text-[14px] bg-teal-600 text-white hover:bg-teal-700 transition-all hover:scale-105 active:scale-95"
            style={{ height: "52px", padding: "4px" }}
            data-testid="btn-price-check"
          >
            <Tag className="w-3.5 h-3.5 mb-0.5" />
            <span>Price Check</span>
          </button></Can>
        </div>
      </div>
    </div>
  );
}
